import React from "react";
import {
  StyleSheet,
  Text,
  TouchableNativeFeedback,
  View,
  TouchableOpacity,
} from "react-native";
import dayjs from "dayjs";
import { useNavigation } from "@react-navigation/native";
import { CrateV2, Gullak, ItemsV2, Returned } from "@assets/svg/MyDeliveries";
import {
  MY_DELIVERY_EVENTS,
  DELIVERY_CODE_EVENTS,
} from "@constants/analyticsEventsCT";
import t from "@constants/localization";
import { colors } from "@constants/theme";
import analytics from "@services/analytics";
import { getTodaysCrateDeliveries } from "@services/api";
import { useSelector } from "react-redux";
import { getReturnAmount } from "../utils";
import { getItemsNotReturnedText, getCrateListFromCrateIds } from "./utils";
import DeliveryBoyInfoComponent from "./DeliveryBoyInfoComponent";
import CratePacketItemsInfo from "../components/CratePacketItemsInfo";
import RightArrow from "@assets/svg/RightArrow";

const PATH = "screens/MyDeliveries/DeliveryReturn/OverView";

function ReturnCard({
  returnDetails,
  date,
  spokeName,
  setInfoTooltipVisible,
  setInfoTooltipData,
  deliveriesData,
  deliveryBoyInfo,
  setSpokeDeliveryReturnDetails,
  setModalVisible,
}) {
  const navigation = useNavigation();
  const returnState = returnDetails?.state;
  const user = useSelector(({ auth }) => auth?.user);
  const { features = {} } = useSelector(({ configReducer }) => configReducer);
  const isCpDb = user?.cp_db_id;
  const returnCardContent = [
    {
      field: "crate",
      icon: <CrateV2 />,
      text: t(`${PATH}/crate`),
      value: returnDetails?.noOfCrates || 0,
      list: returnDetails?.noOfCrates
        ? getCrateListFromCrateIds(returnDetails.crates_list)
        : [],
    },
    {
      field: "item",
      icon: <ItemsV2 />,
      text: t(`${PATH}/item`),
      value: returnDetails?.noOfProducts || 0,
    },
    ...(features?.return_amount
      ? [
          {
            field: "amount",
            icon: <Gullak />,
            text:
              t(`${PATH}/${getReturnAmount(returnDetails?.amount).key}`) || "",
            value:
              (returnDetails?.amount &&
                getReturnAmount(returnDetails?.amount).amount) ||
              0,
            symbol: <Text style={styles.symbol}>{t(`${PATH}/rs`)}</Text>,
          },
        ]
      : []),
  ];

  const handleReturnCardPress = () => {
    navigation.navigate("Returns", {
      date: date || dayjs().format("YYYY-MM-DD"),
      spokeName,
      tab: "Current day",
    });

    analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
      screen: "Delivery Page",
      tab: "Current day",
      section: "Return",
    });
  };

  const onPressInfo = (data) => {
    setInfoTooltipVisible(true);
    setInfoTooltipData(data);
  };

  const cpDbCheck = (Component) => {
    return isCpDb ? null : <Component />;
  };
  function PickupCodeButtonComponent() {
    return (
      <TouchableOpacity
        testID="see_delivery_code"
        onPress={() => onPressDeliveryCode(spokeName)}
      >
        <View style={styles.otpContainer}>
          <Text style={styles.otpText}>रिटर्न कोड देखें</Text>
        </View>
      </TouchableOpacity>
    );
  }
  const onPressDeliveryCode = async (spoke) => {
    try {
      if (deliveryBoyInfo.db_status === "NOT_GENERATED") {
        const { data = {} } = await getTodaysCrateDeliveries();
        const spokeDetails = data.spokeDetails.find(
          (sd) => sd.spokeName === spoke,
        );
        const { db_status } = spokeDetails?.returnDetails?.deliveryBoyInfo;

        setSpokeDeliveryReturnDetails(spokeDetails);
        if (db_status === "NOT_GENERATED") {
          setModalVisible(true);
        } else {
          analytics.trackEvent(DELIVERY_CODE_EVENTS.VIEW_CODE, {
            mode: "GENERATED_VIA_API_FOR_RETURN",
            spoke,
          });
          return navigation.navigate("DeliveryReturnOverview", {
            spokeDetails,
            routeType: deliveryBoyInfo?.route_type,
          });
        }
      } else {
        const spokeDetails = deliveriesData.spokeDetails.find(
          (sd) => sd.spokeName === spoke,
        );
        setSpokeDeliveryReturnDetails(spokeDetails);
        analytics.trackEvent(DELIVERY_CODE_EVENTS.VIEW_CODE, {
          mode: "ALREADY_GENERATED_FOR_RETURN",
          spoke,
        });
        return navigation.navigate("DeliveryReturnOverview", {
          spokeDetails,
          routeType: deliveryBoyInfo?.route_type,
        });
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  return (
    <View style={styles.cardContainer}>
      <TouchableNativeFeedback
        testID="handleReturnCardPress"
        background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
        onPress={() => handleReturnCardPress()}
      >
        <View style={styles.card}>
          <View
            style={[
              styles.headerContainer,
              {
                backgroundColor:
                  returnState === "Return"
                    ? "rgba(0, 171, 89, 0.04)"
                    : "rgba(0, 0, 0, 0.04)",
                flex: 1,
                justifyContent: "space-between",
              },
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Returned status={returnState} />
              <Text
                style={
                  returnState === "Return"
                    ? styles.greenHeaderText
                    : styles.greyHeaderText
                }
              >
                {t(`${PATH}/${returnState}`)}
              </Text>
            </View>
            {returnDetails.state !== "Returned" &&
            deliveryBoyInfo.status !== "NOT_ASSIGNED" &&
            deliveryBoyInfo?.route_type &&
            deliveryBoyInfo?.route_type !== "DELIVERY_PICKUP"
              ? cpDbCheck(PickupCodeButtonComponent)
              : null}
          </View>
          {returnDetails.state === "Return" &&
          deliveryBoyInfo?.route_type &&
          deliveryBoyInfo?.route_type !== "DELIVERY_PICKUP"
            ? cpDbCheck(() => (
                <DeliveryBoyInfoComponent deliveryBoyInfo={deliveryBoyInfo} />
              ))
            : null}
          {returnDetails?.itemsNotReturned ||
          returnDetails?.cratesNotReturned ? (
            <View style={styles.orderNotTaken}>
              <Text style={styles.orderNotTakenText}>
                {getItemsNotReturnedText(returnDetails)}
              </Text>
            </View>
          ) : null}
          <CratePacketItemsInfo
            Content={returnCardContent}
            onPressInfo={onPressInfo}
          />
          <View style={styles.buttonStyle}>
            <View />
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={styles.viewDetailsText}>
                {t(`${PATH}/viewDetails`)}
              </Text>
              <RightArrow
                stroke={colors.extraLightBlack}
                fill={colors.extraLightBlack}
              />
            </View>
          </View>
        </View>
      </TouchableNativeFeedback>
    </View>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  card: {
    backgroundColor: colors.white,
    borderWidth: 0.5,
    borderRadius: 10,
    elevation: 2,
    borderColor: colors.lighterBlack,
  },
  headerContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  buttonStyle: {
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 0.8,
    borderColor: colors.lighterBlack,
    width: "100%",
  },
  orderNotTaken: {
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#FFEEEB",
    marginHorizontal: 16,
    marginVertical: 12,
    borderRadius: 4,
  },
  orderNotTakenText: {
    color: "#FF2E00",
    fontSize: 12,
    lineHeight: 18,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  symbol: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Regular",
    lineHeight: 26,
  },
  viewDetailsText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    lineHeight: 20,
    marginRight: 8,
  },
  greenHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.plain_green,
    marginHorizontal: 4,
  },
  greyHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.lightBlack,
    alignItems: "center",
    marginHorizontal: 4,
  },
  otpContainer: {
    backgroundColor: colors.plain_green,
    paddingHorizontal: 10,
    borderRadius: 40,
    paddingVertical: 2,
  },
  otpText: {
    fontSize: 11,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.white,
    lineHeight: 18,
  },
});

export default ReturnCard;
